import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, TextField, Box, Typography, Container, Paper } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import Header from '../components/Header';

// Shown on the login page after we sign the user out because Firebase refused
// to change their security settings on a sign-in older than a few minutes.
const FRESH_SIGN_IN_NOTICE =
  'Setting up two-factor authentication needs a recent sign-in. Please log in again to continue.';

// Turns a Firebase Auth error from either enrolment step into what to tell the
// user and what they can do about it. `stage` is 'setup' (generating the
// secret and QR code) or 'verify' (submitting the first code).
//
// action is one of:
//   'retry'          the user can simply try again
//   'sign-in-again'  Firebase needs a fresh sign-in; the caller signs them out
//   'restart-setup'  the expired setup session needs a new secret and QR code
function describeMfaError(error, stage) {
  const code = error && error.code ? error.code : '';
  switch (code) {
    case 'auth/requires-recent-login':
      return { action: 'sign-in-again', message: FRESH_SIGN_IN_NOTICE };
    case 'auth/invalid-multi-factor-session':
    case 'auth/code-expired':
      return {
        action: 'restart-setup',
        message: 'This setup session has expired. Restart setup, remove the old entry from your authenticator app, and scan the new QR code.'
      };
    case 'auth/network-request-failed':
      return {
        action: 'retry',
        message: 'Could not reach the sign-in service. Check your connection and try again.'
      };
    case 'auth/too-many-requests':
      return {
        action: 'retry',
        message: 'Too many attempts. Wait a few minutes and try again.'
      };
    case 'auth/invalid-verification-code':
      return {
        action: 'retry',
        message: 'That code was not accepted. Check that the time on your phone is correct and enter the current code.'
      };
    case 'auth/second-factor-already-in-use':
    case 'auth/mfa-enrollment-already-complete':
      return {
        action: 'retry',
        message: 'Two-factor authentication was already enabled on this account. Retry to check its status and continue.'
      };
    default:
      return {
        action: 'retry',
        message: stage === 'setup'
          ? `Two-factor setup could not be started (${code || 'unknown error'}). Please try again. If the problem continues, contact support.`
          : `Two-factor authentication could not be enabled (${code || 'unknown error'}). Please try again. If the problem continues, contact support.`
      };
  }
}

function MFASetup() {
  // 'loading'          checking enrolment and generating the secret
  // 'ready'            secret generated, QR code and code form shown
  // 'already-enrolled' nothing to do
  // 'failed'           setup did not complete; `problem` says why
  const [setupState, setSetupState] = useState('loading');
  const [setupAttempt, setSetupAttempt] = useState(0);
  const [totpSecret, setTotpSecret] = useState(null);
  const [qrUrl, setQrUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  // The current problem, if any: { message, action } from describeMfaError.
  const [problem, setProblem] = useState(null);
  const { currentUser, logout, startMfaEnrollment, finalizeMfaEnrollment, getMfaEnrollmentStatus } = useAuth();
  const navigate = useNavigate();

  // Firebase will not change security settings on an old sign-in, and the Site
  // has no re-authentication step, so the only way through is a fresh login.
  // The login page shows the notice and, after login, returns unenrolled users
  // here automatically. Navigate before signing out: once the user is null,
  // PrivateRoute redirects to /login on its own, without the notice.
  async function signOutForFreshSignIn() {
    navigate('/login', { state: { notice: FRESH_SIGN_IN_NOTICE } });
    try {
      await logout();
    } catch (error) {
      console.error('Could not sign out for a fresh sign-in:', error);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      setSetupState('loading');
      setProblem(null);
      try {
        const enrolled = await getMfaEnrollmentStatus();
        if (cancelled) return;
        if (enrolled) {
          setSetupState('already-enrolled');
          return;
        }

        const secret = await startMfaEnrollment();
        if (cancelled) return;
        setTotpSecret(secret);
        setQrUrl(secret.generateQrCodeUrl(currentUser.email, 'Decode by BIM Analytics'));
        setSecretKey(secret.secretKey);
        setSetupState('ready');
      } catch (error) {
        if (cancelled) return;
        console.error('Error starting MFA enrollment:', error);
        const described = describeMfaError(error, 'setup');
        if (described.action === 'sign-in-again') {
          await signOutForFreshSignIn();
          return;
        }
        setProblem(described);
        setSetupState('failed');
      }
    }

    init();
    return () => { cancelled = true; };
    // The context functions are recreated on every render; listing them would
    // restart setup, and generate a new secret, on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, setupAttempt]);

  const handleVerify = async (e) => {
    e.preventDefault();
    const retrying = Boolean(problem);
    setEnrolling(true);
    setProblem(null);
    try {
      if (retrying && await getMfaEnrollmentStatus()) {
        navigate('/');
        return;
      }
      await finalizeMfaEnrollment(totpSecret, verificationCode);
      navigate('/');
    } catch (error) {
      console.error('Error enrolling MFA:', error);
      const described = describeMfaError(error, 'verify');
      if (described.action === 'sign-in-again') {
        await signOutForFreshSignIn();
        return;
      }
      setProblem(described);
      setVerificationCode('');
      setEnrolling(false);
    }
  };

  const problemBanner = problem && (
    <Typography
      align="center"
      sx={{
        mb: 2,
        color: '#EF4444',
        backgroundColor: '#FEE2E2',
        padding: '0.75rem',
        borderRadius: '6px'
      }}
    >
      {problem.message}
    </Typography>
  );

  if (setupState === 'loading') {
    return (
      <div>
        <Header />
        <Container maxWidth="sm" sx={{ mt: '6rem', textAlign: 'center' }}>
          <Typography sx={{ color: '#6B7280' }}>Setting up two-factor authentication...</Typography>
        </Container>
      </div>
    );
  }

  if (setupState === 'already-enrolled') {
    return (
      <div>
        <Header />
        <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
          <Paper
            elevation={0}
            sx={{
              padding: '3rem',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, color: '#1F2937', fontWeight: 600 }}>
              MFA Already Enabled
            </Typography>
            <Typography sx={{ mb: 3, color: '#6B7280' }}>
              Two-factor authentication is already set up on your account.
            </Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/')}>
              Continue
            </Button>
          </Paper>
        </Container>
      </div>
    );
  }

  if (setupState === 'failed') {
    return (
      <div>
        <Header />
        <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
          <Paper
            elevation={0}
            sx={{
              padding: '3rem',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ mb: 2, color: '#1F2937', fontWeight: 600 }}>
              Set Up Two-Factor Authentication
            </Typography>
            {problemBanner}
            {['retry', 'restart-setup'].includes(problem.action) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSetupAttempt((attempt) => attempt + 1)}
                sx={{ mt: 1 }}
              >
                Try Again
              </Button>
            )}
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
        <Paper
          elevation={0}
          sx={{
            padding: '3rem',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{ mb: 2, color: '#1F2937', fontWeight: 600 }}
          >
            Set Up Two-Factor Authentication
          </Typography>
          <Typography
            align="center"
            sx={{ mb: 4, color: '#6B7280' }}
          >
            Scan the QR code below with your authenticator app (Google Authenticator, Authy, etc.), then enter the 6-digit code to verify.
          </Typography>

          {problemBanner}
          {problem?.action === 'restart-setup' && (
            <Button onClick={() => setSetupAttempt((attempt) => attempt + 1)} disabled={enrolling}>
              Restart setup
            </Button>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                mb: 2
              }}
            >
              <QRCodeSVG value={qrUrl} size={200} />
            </Box>
            <Typography
              sx={{
                color: '#6B7280',
                fontSize: '0.8rem',
                mb: 1
              }}
            >
              Can't scan? Enter this key manually:
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                backgroundColor: '#F3F4F6',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                color: '#1F2937',
                wordBreak: 'break-all',
                textAlign: 'center',
                userSelect: 'all'
              }}
            >
              {secretKey}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleVerify} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              type="text"
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(val);
              }}
              required
              fullWidth
              variant="outlined"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
              }}
              autoFocus
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={verificationCode.length !== 6 || enrolling || problem?.action === 'restart-setup'}
              sx={{ mt: 1, py: 1.5, fontSize: '1rem', fontWeight: 500 }}
            >
              {enrolling ? 'Verifying...' : problem?.action === 'retry' ? 'Retry verification' : 'Verify & Enable MFA'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </div>
  );
}

export default MFASetup;
